/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import View from "App/view/view";
import {mat3, mat4, vec3, vec4} from "gl-matrix";
import BoundingBox from "App/utils/bounding_box";
import RenderDocument from "App/render_document";
import IScene from "js_interface/src/scene_interface";
import {CameraTypeEnum} from "js_interface/src/modules/view_controls/scene_view_controls_enums";
import {IProjectionMatrices} from "js_interface/src/modules/print/scene_print_interface";

export default
class Camera {

    private static cameraUID: number = 0;

    protected _sceneAPI: IScene;
    protected _view: View;

    protected viewMat: mat4 = mat4.create();
    protected _viewMatInvalid: boolean = true;

    protected projectionMatrix: mat4 = mat4.create();
    protected _projMatInvalid: boolean = true;
    protected _userDefinedProjMatrices: IProjectionMatrices = null;

    protected target: vec3 = vec3.create();
    protected eye: vec3 = vec3.create();

    protected upVector: vec3;
    protected fieldOfView: number = -1.0;
    protected aspectRatio: number = 1.0;

    protected znear: number = 0;
    protected zfar: number = 0;

    // Added for Orthogonal Projection and Shadow map calculations
    protected _lightComponents = null;
    protected _lightComponentsValid = false;
    protected _lightPosition = vec3.create();
    protected _lightProjection = mat4.create();
    protected _lightView = mat4.create();

    protected _pgc: any; // for garbage collector invocation reduction

    private _ID: number;
    private _document: RenderDocument;
    private _upVectorDef: vec3 = vec3.fromValues(0.0, 1.0, 0.0);

    private _boundingBox: BoundingBox = new BoundingBox();
    private _boundingBoxForNPC: BoundingBox = new BoundingBox();

    private forcedNearValue: number = 0;

    private forcedFarValue: number = 0;

    // Temporary variables
    private _tmpVec3: vec3 = vec3.create();
    private _tmpVec3B: vec3 = vec3.create();
    private _tmpMat4: mat4 = mat4.create();

    /** @type {{projection: mat4, view: mat4, maxLength: number}} */
    private _lightOrthogonalComponents: any = {};
    private _lightOrthogonalComponentsValid: boolean = false;
    private _lightOrthogonalMatrix: mat4 = mat4.create();
    private _lightOrthogonalMatrixValid: boolean = false;
    private _floorOrthogonalMatrix: mat4 = mat4.create();
    private _floorOrthogonalMatrixValid: boolean = false;

    private _useOrthoProjection: boolean = false;

    private _backuped: boolean = false;
    private _backup: any = {};

    constructor(aView: View) {

        this._ID = Camera.cameraUID++;

        this._view = aView;
        this._document = aView.getDocument();
        this._sceneAPI = this._document.getSceneAPI();

        this.upVector = vec3.clone(this._upVectorDef);

        // Register to test manipulator transformations
        this._sceneAPI.activityModule.registerObserver(this);

        this._pgc = {
            VPMat: mat4.create(),
            VMat: mat4.create(),
            VMatInv: mat4.create(),
            projmatInv: mat4.create(),
            vpm3it: mat3.create(),
            projmat: mat4.create(),
            projmatInf: mat4.create(),
            centerInCs: vec4.create(),
            customVMatrix: mat4.create(),
            customVPMatrix: mat4.create(),
            camPos: vec3.create(),
            camTarget: vec3.create(),
            vec4A: vec4.create()
        };
    }

    /***
     * Function creates new Camera class instance and initialize selected members based on the cloned instance.
     * The cloned instance has its own unique camera id
     * @returns {Camera} new camera instance cloned from this camera instance
     */
    public clone(): Camera {
        // create a new camera class
        let newCam = new Camera(this._view);

        // init the values for the new camera from this cloned camera instance
        newCam.viewMat = mat4.clone(this.viewMat);
        newCam.target = vec3.clone(this.target);
        newCam.eye = vec3.clone(this.eye);
        newCam.projectionMatrix = mat4.clone(this.projectionMatrix);
        newCam.upVector = vec3.clone(this.upVector);

        newCam.fieldOfView = this.fieldOfView;
        newCam.aspectRatio = this.aspectRatio;

        newCam._boundingBox = this._boundingBox.clone();
        newCam._boundingBoxForNPC = this._boundingBoxForNPC.clone();

        newCam.znear = this.znear;
        newCam.zfar = this.zfar;

        newCam.forcedNearValue = this.forcedNearValue;
        newCam.forcedFarValue = this.forcedFarValue;
        newCam._useOrthoProjection = this._useOrthoProjection;

        this._projMatInvalid = true;

        return newCam;
    }

    public getUseOrthographicProjection(): boolean {
        return this._useOrthoProjection;
    }

    public setUseOrthographicProjection(aValue: boolean) {
        let oldValue = this._useOrthoProjection;

        this._useOrthoProjection = aValue;

        this._invalidatePerspectiveProjMatrix();

        return oldValue;
    }

    /**
     * Save all important values
     */
    public backup() {
        // Allocate memory only once
        if (!this._backuped) {
            this._backuped = true;
            this._backup.viewMatBackup = mat4.clone(this.viewMat);
            this._backup.targetBackup = vec3.clone(this.target);
            this._backup.eyeBackup = vec3.clone(this.eye);
            this._backup.projectionMatrixBackup = mat4.clone(this.projectionMatrix);
            this._backup.upVectorBackup = vec3.clone(this.upVector);
            this._backup.boundingBoxBackup = this._boundingBox.clone();
            this._backup.boundingBoxForNPCBackup = this._boundingBoxForNPC.clone();
        }
        else {
            mat4.copy(this._backup.viewMatBackup, this.viewMat);
            vec3.copy(this._backup.targetBackup, this.target);
            vec3.copy(this._backup.eyeBackup, this.eye);
            mat4.copy(this._backup.projectionMatrixBackup, this.projectionMatrix);
            vec3.copy(this._backup.upVectorBackup, this.upVector);
            this._backup.boundingBoxBackup.copyFrom(this._boundingBox);
            this._backup.boundingBoxForNPCBackup.copyFrom(this._boundingBoxForNPC);
        }

        // Variables without memory allocation
        this._backup.fovBackup = this.fieldOfView;
        this._backup.aspectRatioBackup = this.aspectRatio;
        this._backup.znearBackup = this.znear;
        this._backup.forcedNearValueBackup = this.forcedNearValue;
        this._backup.zfarBackup = this.zfar;
        this._backup.forcedFarValueBackup = this.forcedFarValue;
        this._backup.projMatInvalidBackup = this._projMatInvalid;
        this._backup.viewMatInvalidBackup = this._viewMatInvalid;
    }

    /**
     * Restore all important values.
     */
    public restore() {
        if (!this._backuped) {
            return;
        }

        mat4.copy(this.viewMat, this._backup.viewMatBackup);
        vec3.copy(this.target, this._backup.targetBackup);
        vec3.copy(this.eye, this._backup.eyeBackup);
        mat4.copy(this.projectionMatrix, this._backup.projectionMatrixBackup);
        vec3.copy(this.upVector, this._backup.upVectorBackup);
        this._boundingBox.copyFrom(this._backup.boundingBoxBackup);
        this._boundingBoxForNPC.copyFrom(this._backup.boundingBoxForNPCBackup);

        // Variables without memory allocation
        this.fieldOfView = this._backup.fovBackup;
        this.aspectRatio = this._backup.aspectRatioBackup;
        this.znear = this._backup.znearBackup;
        this.forcedNearValue = this._backup.forcedNearValueBackup;
        this.zfar = this._backup.zfarBackup;
        this.forcedFarValue = this._backup.forcedFarValueBackup;
        this._projMatInvalid = this._backup.projMatInvalidBackup;
        this._viewMatInvalid = this._backup.viewMatInvalidBackup;
    }

    public _invalidatePerspectiveProjMatrix() {
        this._projMatInvalid = true;
    }

    public _invalidateViewMatrix() {
        this._viewMatInvalid = true;
    }

    /** Invalidate light matrix needed for calculating shadow maps */
    public invalidateLightMatrix() {
        this._lightOrthogonalMatrixValid = false;
        this._lightOrthogonalComponentsValid = false;
    }

    /** Invalidate light components, for instance light bounding box or position */
    public invalidateLight() {
        this._lightComponentsValid = false;
        this.invalidateLightMatrix();
    }

    /** Invalidate floor matrix (for instance when manipulating with model or bb changed) */
    public invalidateFloorMatrix() {
        this._floorOrthogonalMatrixValid = false;
    }

    public getNearValue(): number {
        return this.znear;
    }

    public getFarValue(): number {
        return (this.forcedFarValue > 0) ? this.forcedFarValue : this.zfar;
    }

    public getCameraType(): CameraTypeEnum {
        return CameraTypeEnum.PERSPECTIVE;
    }

    public getID(): number {
        return this._ID;
    }

    public getViewMatrix3IT(): mat3 {
        if (this._viewMatInvalid) {
            this._recomputeViewMatrix();
            this._viewMatInvalid = false;
        }

        mat3.normalFromMat4(this._pgc.vpm3it, this.viewMat);
        return this._pgc.vpm3it;
    }

    public setAspectRatio(aValue: number) {
        this.aspectRatio = aValue;
        this._invalidatePerspectiveProjMatrix();
    }

    public getAspectRatio(): number {
        return this.aspectRatio;
    }

    public viewResized(aAspectRatio: number) {
        this.setAspectRatio(aAspectRatio);
    }

    public setFieldOfView(aFieldOfView: number) {
        this.fieldOfView = aFieldOfView;
        this._invalidatePerspectiveProjMatrix();
    }

    public getFieldOfView(): number {
        return this.fieldOfView;
    }

    public onManipulatorChanged() {
        this.invalidateFloorMatrix();
    }

    public onBoundingBoxUpdated(aBoundingBox: BoundingBox, aBoundingBoxForNPC: BoundingBox) {
        this._boundingBox.init();
        this._boundingBox.extendByBBox(aBoundingBox);

        this._boundingBoxForNPC.init();
        this._boundingBoxForNPC.extendByBBox(aBoundingBoxForNPC);

        this._invalidatePerspectiveProjMatrix();
        this.invalidateLight();
        this.invalidateFloorMatrix();
    }

    /** Called when sun position has changed */
    public sunUpdated() {
        this.invalidateLight();
    }

    public setCameraUpVector(aUpVector: vec3) {
        vec3.copy(this.upVector, aUpVector);
        this._invalidateViewMatrix();
        this.invalidateLightMatrix();
    }

    public resetCameraUpVector() {
        this.upVector = vec3.clone(this._upVectorDef);
        this._invalidateViewMatrix();
        this._invalidatePerspectiveProjMatrix();
    }

    public getCameraUpVectorRef(): vec3 {
        return this.upVector;
    }

    // camera view vector (NOT normalized)
    public getViewVector(): vec3 {
        vec3.subtract(this._tmpVec3, this.target, this.eye);
        return this._tmpVec3;
    }

    /**
     * Normalized camera view vector
     * @returns {vec3}
     */
    public getCameraDirection(): vec3 {
        vec3.subtract(this._tmpVec3, this.target, this.eye);
        vec3.normalize(this._tmpVec3, this._tmpVec3);
        return this._tmpVec3;
    }

    public getCameraPosition(): vec3 {
        vec3.copy(this._pgc.camPos, this.eye);
        return this._pgc.camPos;
    }

    public setCameraPosition(aPosition: vec3) {
        vec3.copy(this.eye, aPosition);
        this._invalidateViewMatrix();
        this._invalidatePerspectiveProjMatrix(); // this is needed becaue the near/far is changed as the eya and target have changed
    }

    public getCameraTarget(): vec3 {
        vec3.copy(this._pgc.camTarget, this.target);
        return this._pgc.camTarget;
    }

    public setCameraTarget(aTarget: vec3) {
        vec3.copy(this.target, aTarget);
        this._invalidateViewMatrix();
        this._invalidatePerspectiveProjMatrix(); // this is needed becaue the near/far is changed as the eya and target have changed
    }

    public setCameraPositionAndTarget(aPosition: vec3, aTarget: vec3) {
        vec3.copy(this.eye, aPosition);
        vec3.copy(this.target, aTarget);

        this._invalidateViewMatrix();
        this._invalidatePerspectiveProjMatrix(); // this is needed becaue the near/far is changed as the eya and target have changed
    }

    public getProjectionMatrixRef(): mat4 {
        if (this._projMatInvalid) {
            this._computeProjectionMatrix();
            this._projMatInvalid = false;
        }

        return this.projectionMatrix;
    }

    public getProjectionMatrixInv(): mat4 {
        if (this._projMatInvalid) {
            this._computeProjectionMatrix();
            this._projMatInvalid = false;
        }

        mat4.invert(this._pgc.projmatInv, this.projectionMatrix);
        return this._pgc.projmatInv;
    }

    public forceNearValue(aValue: number) {
        this.forcedNearValue = aValue;

        this._invalidatePerspectiveProjMatrix();
    }

    public forceFarValue(aValue: number) {
        this.forcedFarValue = aValue;

        this._invalidatePerspectiveProjMatrix();
    }

    /**
     * Bottom to Top around bounding box
     *
     * @return {mat4}
     */
    public getFloorOrthogonalMatrix(): mat4 {
        // Cache
        if (this._floorOrthogonalMatrixValid) {
            return this._floorOrthogonalMatrix;
        }

        // BB of model
        let bb = this._document.getBoundingBoxFromVisibleObjectsForNPC();
        bb.transformMat4(this._sceneAPI.activityModule.getCurrentManipulatorTransformation());
        let center = bb.getCenter();
        let size = bb.getDiameter();

        // Calculate light position and shadow map size, projection etc.
        // tmpVec3 ... position
        // tmpMat4 ... projection
        // floorOrthogonalMatrix ... view at beginning

        this._tmpVec3[0] = center[0] + 0.0001;
        this._tmpVec3[1] = bb.getMin()[1] - 1.0;
        this._tmpVec3[2] = center[2];

        mat4.ortho(this._tmpMat4, -size / 2.0, size / 2.0, -size / 2.0, size / 2.0, 0.1, size + 1);
        mat4.lookAt(this._floorOrthogonalMatrix, this._tmpVec3, center, this.upVector);
        mat4.multiply(this._floorOrthogonalMatrix, this._tmpMat4, this._floorOrthogonalMatrix);

        this._floorOrthogonalMatrixValid = true;
        return this._floorOrthogonalMatrix;
    }

    /**
     * Used for PCF Shadow maps
     *
     * @return {mat4}
     */
    public getLightOrthogonalMatrix(): mat4 {
        if (this._lightOrthogonalMatrixValid) {
            return this._lightOrthogonalMatrix;
        }

        let light = this.getLightOrthogonalMatrixSeparate();
        mat4.multiply(this._lightOrthogonalMatrix, light.projection, light.view);

        this._lightOrthogonalMatrixValid = true;
        return this._lightOrthogonalMatrix;
    }

    /**
     * Needed for Variance Shadow Maps
     *
     * @return {{projection: mat4, view: mat4, maxLength: number}}
     */
    public getLightOrthogonalMatrixSeparate(): {projection: mat4, view: mat4, maxLength: number} {
        // Cache
        if (this._lightOrthogonalComponentsValid) {
            return this._lightOrthogonalComponents;
        }

        // Get light components
        let components = this.getLightComponents();
        let size = components.boundary.getDiameter();
        let center = components.boundary.getCenter();
        let sunPosition = components.light;

        // Calculate light position and shadow map size, projection etc.
        mat4.ortho(this._lightProjection, -size / 2.0, size / 2.0, -size / 2.0, size / 2.0, 0.1, size);
        mat4.lookAt(this._lightView, sunPosition, center, this.upVector);

        this._lightOrthogonalComponents = {
            projection: this._lightProjection,
            view: this._lightView,
            maxLength: Math.floor(size) + 1
        };

        this._lightOrthogonalComponentsValid = true;
        return this._lightOrthogonalComponents;
    }

    public getLightComponents(): {light: vec3, boundary: BoundingBox} {
        // Cache
        if (this._lightComponentsValid) {
            return this._lightComponents;
        }

        let bb = this._document.getBoundingBoxFromVisibleObjectsForNPC();
        bb.transformMat4(this._sceneAPI.activityModule.getCurrentManipulatorTransformation());
        let half = bb.getDiameter() / 2.0;
        let center = bb.getCenter();

        // Compute light
        let lightDirection = this._sceneAPI.lightingModule.getSunDirection();
        this._lightPosition[0] = center[0] + lightDirection[0] * half;
        this._lightPosition[1] = center[1] + lightDirection[1] * half;
        this._lightPosition[2] = center[2] + (lightDirection[2] + 0.0000001) * half;  // Fix if sun is over center

        this._lightComponents = {
            light: this._lightPosition,
            boundary: bb
        };

        this._lightComponentsValid = true;
        return this._lightComponents;
    }

    public computeProjectionMatrixWithFarAtInfinity(): mat4 {
        // the matrix has the indexed organized to rows/columns like that:
        // 0    4   8  12
        // 1    5   9  13
        // 2    6  10  14
        // 3    7  11  15
        mat4.identity(this._pgc.projmatInf);

        let range = Math.tan(this.fieldOfView / 2.0) * this.znear;
        let left = -range * this.aspectRatio;
        let right = range * this.aspectRatio;
        let bottom = -range;
        let top = range;

        let ep = 0.000000059604644775390625;

        // http://http.developer.nvidia.com/GPUGems/gpugems_ch09.html
        // and http://www.gamasutra.com/view/feature/131351/the_mechanics_of_robust_stencil_.php?print=1
        this._pgc.projmatInf[0] = (2.0 * this.znear) / (right - left);
        this._pgc.projmatInf[8] = (right + left) / (right - left);
        this._pgc.projmatInf[5] = (2.0 * this.znear) / (top - bottom);
        this._pgc.projmatInf[9] = (top + bottom) / (top - bottom);
        this._pgc.projmatInf[10] = ep - 1.0;
        this._pgc.projmatInf[11] = -1.0;
        this._pgc.projmatInf[14] = (ep - 2.0) * this.znear;

        return this._pgc.projmatInf;
    }

    public computeProjectionMatrix(): mat4 {

        if (this._useOrthoProjection) {
            if (this._userDefinedProjMatrices !== null) {
                this._pgc.projmat = mat4.clone(this._userDefinedProjMatrices.ortho);
            }
            else {
                let dist = vec3.distance(this.target, this.eye);

                // based on https://stackoverflow.com/questions/48187416/how-to-switch-between-perspective-and-orthographic-cameras-keeping-size-of-desir
                // also based on https://docs.unity3d.com/Manual/FrustumSizeAtDistance.html

                // https://www.google.cz/search?q=tangent+uhlu&oq=tangent+uhlu&aqs=chrome..69i57j0l3.2776j0j7&sourceid=chrome&ie=UTF-8
                // we can compute the near plane height from tan(angle) = opposite/adjacent, where as in our case it is angle = fov / 2
                // and to compute the opposite we have opposite = tan(fov / 2) * dist to near plane
                let oppositeToCamera = Math.tan(this.fieldOfView / 2.0) * dist;
                let size_y = oppositeToCamera * 2.0;
                let size_x = oppositeToCamera * 2.0 * this.aspectRatio;

                mat4.ortho(this._pgc.projmat, -size_x / 2, size_x / 2, -size_y / 2, size_y / 2, -1, this.zfar);
            }
        }
        else {
            if (this._userDefinedProjMatrices !== null) {
                this._pgc.projmat = mat4.clone(this._userDefinedProjMatrices.perspective);
            }
            else {
                mat4.perspective(this._pgc.projmat, this.fieldOfView, this.aspectRatio, this.znear, this.zfar);
            }
        }

        return this._pgc.projmat;
    }

    public setUserDefinedProjectionMatrices(aProjectionMatrices: IProjectionMatrices) {
        this._userDefinedProjMatrices = aProjectionMatrices;
        this._invalidatePerspectiveProjMatrix();
    }

    public getUserDefinedProjectionMatrices(): IProjectionMatrices {
        return this._userDefinedProjMatrices;
    }

    public getViewProjectionMatrix(): mat4 {
        if (this._viewMatInvalid) {
            this._recomputeViewMatrix();
            this._viewMatInvalid = false;
        }

        if (this._projMatInvalid) {
            this._computeProjectionMatrix();
            this._projMatInvalid = false;
        }

        mat4.multiply(this._pgc.VPMat, this.projectionMatrix, this.viewMat);

        return this._pgc.VPMat;
    }

    public getViewMatrix(): mat4 {
        if (this._viewMatInvalid) {
            this._recomputeViewMatrix();
            this._viewMatInvalid = false;
        }

        mat4.copy(this._pgc.VMat, this.viewMat);
        return this._pgc.VMat;
    }

    public getViewMatrixInv(): mat4 {
        if (this._viewMatInvalid) {
            this._recomputeViewMatrix();
            this._viewMatInvalid = false;
        }

        mat4.invert(this._pgc.VMatInv, this.viewMat);
        return this._pgc.VMatInv;
    }

    public getCustomViewMatrix(aPosition: vec3, aTarget: vec3): mat4 {
        mat4.lookAt(this._pgc.customVMatrix, aPosition, aTarget, this.upVector);
        return this._pgc.customVMatrix;
    }

    public getCustomViewProjectionMatrix(aPosition: vec3, aTarget: vec3): mat4 {
        mat4.lookAt(this._pgc.customVPMatrix, aPosition, aTarget, this.upVector);

        mat4.multiply(this._pgc.customVPMatrix, this.projectionMatrix, this._pgc.customVPMatrix);

        return this._pgc.customVPMatrix;
    }

    public restrictPitch(aEye: vec3, aNewEye: vec3, aRadX: number, aRadY: number) {
        if (this._viewMatInvalid) {
            this._recomputeViewMatrix();
            this._viewMatInvalid = false;
        }

        let oldView = vec3.create();
        let newView = vec3.create();

        vec3.subtract(oldView, this.target, aEye);
        vec3.normalize(oldView, oldView);

        vec3.subtract(newView, this.target, aNewEye);
        vec3.normalize(newView, newView);

        let oldAngle = Math.acos(vec3.dot(this.upVector, oldView));
        let difAngle = Math.acos(vec3.dot(oldView, newView));

        let dySign;
        if (aRadY > 0) {
            dySign = 1;
        } else {
            dySign = -1;
        }

        let testedAngle = oldAngle + difAngle * dySign;

        if (testedAngle > Math.PI || testedAngle < 0) {
            let identity = mat4.create();
            mat4.identity(identity);

            this.viewMat = mat4.create();
            mat4.translate(this.viewMat, identity, this.target);

            mat4.rotate(this.viewMat, this.viewMat, -aRadX, this.upVector);

            let targetNeg = vec3.create();
            vec3.negate(targetNeg, this.target);
            mat4.translate(this.viewMat, this.viewMat, targetNeg);

            vec3.transformMat4(aNewEye, aEye, this.viewMat);
        }

        return aNewEye;
    }

    /**
     *  Returns sinus of the camera elevation (rotation around x axis).
     */
    public getXRotationAngle(): number {
        let sinAlpha = (this.target[1] - this.eye[1]) / vec3.length(this.getViewVector());

        // let alpha = Math.asin(sinAlpha) * 180.0 / Math.PI;
        return sinAlpha; // really returns a sinus of a camera elevation angle
    }

    protected _recomputeViewMatrix() {
        mat4.lookAt(this.viewMat, this.eye, this.target, this.upVector);
    }

    /**
     * Process changes of other modules; Follows SceneAPI Abstract module API
     *
     * @param aJsonObject
     */
    protected notifySettings(aChangedSettings: any, aPrevisusSettings: any) {
        //
    }

    private _computeNearAndFar(): {znear: number, zfar: number} {
        if (this._viewMatInvalid) {
            this._recomputeViewMatrix();
            this._viewMatInvalid = false;
        }

        let zfarLoc;
        let znearLoc;

        let center = this._boundingBox.getCenter();
        let radius = this._boundingBox.getDiameter() / 2;

        vec4.set(this._pgc.vec4A, center[0], center[1], center[2], 1);
        vec4.transformMat4(this._pgc.centerInCs, this._pgc.vec4A, this.viewMat);  // to camera space

        let cameraDistance = vec3.distance(center, this.eye);

        znearLoc = cameraDistance - radius;
        zfarLoc  = cameraDistance + radius;

        // check if zmin is less than zero
        let zmin = 0.01;
        if (this._boundingBox.containsPoint(this.eye)) {
            // we are inside the bounding sphere of scene
            // and here to compute the near plane value we use the bounding box for near plane computation
            // i.e. without contributeToNearPlaneClipping property set to false (e.g. HeightMapTile elements)
            // the max(x, 10) is because for small models we need smaller near plane
            znearLoc = (this._boundingBoxForNPC.getDiameter() / 2) / Math.max(zfarLoc, 10.0);
        }
        else if (znearLoc < 0) { // znerLoc < 0 is for large thin models, we should use not the radius, but distance to closest point of bbox
            // really magic here because for large models we need to only hardly compute the real distance from the model in order to
            // set the near plane as far as possible so we get flickering (merced model)
            // this should be further tweaked, maybe the getClosesPoint should be improved to return not point in corner, but instead
            // point on plane (one of the sixs) on a line perpendicular to the plane and passing though camera position
            znearLoc = vec3.distance(this._boundingBox.getClosestPoint(this.eye), this.eye) / (2 * Math.pow(Math.log(zfarLoc), 2));
        }

        // keep the minimal zmin
        if (znearLoc < zmin) {
            znearLoc = zmin;
        }

        // check equality of near and far
        if (znearLoc === zfarLoc) {
            zfarLoc = zfarLoc + 10.0;
        }

        return {
            znear: (this.forcedNearValue > 0) ? this.forcedNearValue : znearLoc,
            zfar: (this.forcedFarValue > 0) ? this.forcedFarValue : zfarLoc
        };
    }

    private _computeProjectionMatrix() {
        let nf = this._computeNearAndFar();

        this.znear = nf.znear;
        this.zfar = nf.zfar;

        if (this._sceneAPI.shadowsModule.getShadowVolumesEnabled()) {
            mat4.copy(this.projectionMatrix, this.computeProjectionMatrixWithFarAtInfinity());
        } else {
            mat4.copy(this.projectionMatrix, this.computeProjectionMatrix());
        }
    }
}
